import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Section, Container } from '@/components/shared/Section';
import { GlassPanel } from '@/components/shared/GlassPanel';
import { SignalChip } from '@/components/shared/SignalChip';
import { AnimatedReveal } from '@/components/shared/AnimatedReveal';
import { SEO } from '@/components/shared/SEO';
import { fadeInUp, staggerContainer, viewportOnce } from '@/lib/motion';
import { Search, ArrowRight, Filter, ExternalLink, Eye, Smartphone, Globe } from 'lucide-react';

const toolsData = {
  '/tools/meta-ads-search': {
    title: 'Free Meta Ads Search',
    desc: 'Search the Meta Ad Library for any brand or competitor. See active Facebook and Instagram ads instantly.',
    icon: Eye,
    placeholder: 'Enter a brand name or domain...',
    badge: 'Meta',
    examples: ['nike.com', 'allbirds', 'gymshark', 'glossier'],
    upgrade: 'Get real-time monitoring, alerts, and analytics with RADAR Pro.',
  },
  '/tools/tiktok-ads-search': {
    title: 'Free TikTok Ads Search',
    desc: 'Search the TikTok Ad Library for competitor campaigns. Discover trending creatives and ad strategies.',
    icon: Smartphone,
    placeholder: 'Enter a brand name or keyword...',
    badge: 'TikTok',
    examples: ['shein', 'fashionnova', 'skims', 'rare beauty'],
    upgrade: 'Unlock automated TikTok ad monitoring with RADAR Pro.',
  },
  '/tools/google-ads-search': {
    title: 'Free Google Ads Search',
    desc: 'Search for competitor Google Ads. See search ads, shopping campaigns, and display creatives.',
    icon: Globe,
    placeholder: 'Enter a brand name or keyword...',
    badge: 'Google',
    examples: ['away luggage', 'casper mattress', 'warby parker', 'dollar shave club'],
    upgrade: 'Get full Google Ads intelligence with RADAR Pro.',
  },
};

const sampleResults = [
  { brand: 'Sample Brand', type: 'Video Ad', platform: 'Feed', status: 'Active', started: '3 days ago' },
  { brand: 'Sample Brand', type: 'Carousel Ad', platform: 'Stories', status: 'Active', started: '1 week ago' },
  { brand: 'Sample Brand', type: 'Image Ad', platform: 'Feed', status: 'Active', started: '2 weeks ago' },
];

export default function ToolPageNew() {
  const location = useLocation();
  const data = toolsData[location.pathname];
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState(false);

  if (!data) {
    return (
      <Section hero>
        <Container>
          <h1 className="text-2xl font-display font-bold text-foreground">Tool not found</h1>
          <Link to="/" className="text-lime-400 hover:text-lime-300 mt-4 inline-block">Go home</Link>
        </Container>
      </Section>
    );
  }

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) setSearched(true);
  };

  return (
    <>
      <SEO
        title={data.title}
        description={data.desc}
        canonical={location.pathname}
      />
      {/* Hero + Search */}
      <Section hero className="relative overflow-hidden">
        <div className="absolute inset-0 radar-glow-hero opacity-40" />
        <Container className="relative z-10">
          <AnimatedReveal>
            <div className="max-w-2xl mx-auto text-center">
              <SignalChip variant="lime" className="mb-4">Free Tool</SignalChip>
              <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-foreground mb-4">
                {data.title}
              </h1>
              <p className="text-base text-muted-foreground mb-8">{data.desc}</p>

              {/* Search form */}
              <form onSubmit={handleSearch} className="relative" data-testid={`tools-${data.badge.toLowerCase()}-search-form`}>
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-2 focus-within:border-lime-500/30">
                  <Search className="h-5 w-5 text-muted-foreground ml-2" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={data.placeholder}
                    className="flex-1 bg-transparent text-sm text-foreground placeholder-muted-foreground outline-none py-2"
                    data-testid={`tools-${data.badge.toLowerCase()}-ads-search-input`}
                  />
                  <button
                    type="submit"
                    className="px-5 py-2.5 rounded-lg bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors"
                    data-testid={`tools-${data.badge.toLowerCase()}-search-button`}
                  >
                    Search
                  </button>
                </div>
              </form>

              {/* Example queries */}
              <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                <span className="text-xs text-muted-foreground">Try:</span>
                {data.examples.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => { setQuery(ex); setSearched(true); }}
                    className="text-xs text-foreground/60 hover:text-lime-400 transition-colors px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06]"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          </AnimatedReveal>
        </Container>
      </Section>

      {/* Results area */}
      <Section dense>
        <Container>
          {!searched ? (
            <AnimatedReveal>
              <GlassPanel className="p-12 text-center">
                <Search className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-display font-semibold text-foreground mb-2">Search for a brand to get started</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Enter a brand name or domain above to search {data.badge} ads. Results will appear here.
                </p>
              </GlassPanel>
            </AnimatedReveal>
          ) : (
            <AnimatedReveal>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">Showing sample results for <span className="text-foreground font-medium">"{query}"</span></p>
                  <button className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                    <Filter className="h-3.5 w-3.5" /> Filter
                  </button>
                </div>

                {sampleResults.map((r, i) => (
                  <GlassPanel key={i} hover className="p-5 flex items-center gap-6">
                    <div className="h-16 w-24 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                      <data.icon className="h-6 w-6 text-muted-foreground/40" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-foreground">{r.brand}</p>
                        <SignalChip className="text-[9px]">{r.type}</SignalChip>
                      </div>
                      <p className="text-xs text-muted-foreground">{r.platform} &middot; {r.status} &middot; Started {r.started}</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-muted-foreground/40" />
                  </GlassPanel>
                ))}

                {/* Upgrade CTA */}
                <GlassPanel className="p-8 text-center border-lime-500/10">
                  <p className="text-sm text-foreground mb-2">Want more? This is just a preview.</p>
                  <p className="text-xs text-muted-foreground mb-6">{data.upgrade}</p>
                  <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors">
                    Start free trial <ArrowRight className="h-4 w-4" />
                  </Link>
                </GlassPanel>
              </div>
            </AnimatedReveal>
          )}
        </Container>
      </Section>
    </>
  );
}
